import React, { useEffect, useState } from "react";
import { Modal, Input } from "antd";

interface TerminalFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  loading?: boolean;
  initialData?: any | null; // null = Add mode, object = Edit mode
}

const TerminalFormModal: React.FC<TerminalFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  initialData = null,
}) => {
  const isEdit = !!initialData;

  const [providerName, setProviderName] = useState("");
  const [providerIdentifier, setProviderIdentifier] = useState("");
  const [apiUrl, setApiUrl] = useState("");
  const [serverIp, setServerIp] = useState("");
  const [iframeUrl, setIframeUrl] = useState("");

  const [errors, setErrors] = useState<any>({});

  // Load initial data on Edit
  useEffect(() => {
    if (initialData) {
      setProviderName(initialData.provider_name || "");
      setProviderIdentifier(initialData.provider_identifier || "");
      setApiUrl(initialData.api_url || "");
      setServerIp(initialData.server_ip || "");
      setIframeUrl(initialData.iframe_url || "");
    } else {
      setProviderName("");
      setProviderIdentifier("");
      setApiUrl("");
      setServerIp("");
      setIframeUrl("");
    }
  }, [initialData]);

  const validate = () => {
    const newErrors: any = {};

    if (!providerName.trim()) newErrors.providerName = "Required";

    // providerIdentifier is required ONLY in Add mode
    if (!isEdit && !providerIdentifier.trim())
      newErrors.providerIdentifier = "Required";

    if (!apiUrl.trim()) newErrors.apiUrl = "Required";
    if (!serverIp.trim()) newErrors.serverIp = "Required";
    if (!iframeUrl.trim()) newErrors.iframeUrl = "Required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    // Build payload
    const payload: any = {
      provider_name: providerName,
      api_url: apiUrl,
      server_ip: serverIp,
      iframe_url: iframeUrl,
    };

    // Add provider_identifier ONLY IN ADD MODE
    if (!isEdit) {
      payload.provider_identifier = providerIdentifier;
    }

    onSubmit(payload);
  };

  return (
    <Modal
      title={isEdit ? "Edit Terminal" : "Add New Terminal"}
      open={open}
      onCancel={onClose}
      footer={null}
    >
      <div className="flex flex-col gap-3">
        {/* Provider Name */}
        <div>
          <p className="text-sm font-medium">Provider Name*</p>
          <Input
            value={providerName}
            onChange={(e) => setProviderName(e.target.value)}
            placeholder="Enter Provider Name"
          />
          {errors.providerName && (
            <p className="text-red-500 text-sm">{errors.providerName}</p>
          )}
        </div>

        {/* Provider Identifier (DISABLED IN EDIT MODE) */}
        <div>
          <p className="text-sm font-medium">Provider Identifier*</p>
          <Input
            value={providerIdentifier}
            disabled={isEdit} // disable during edit
            onChange={(e) => setProviderIdentifier(e.target.value)}
            placeholder="Enter Provider Identifier"
          />
          {errors.providerIdentifier && !isEdit && (
            <p className="text-red-500 text-sm">{errors.providerIdentifier}</p>
          )}
        </div>

        {/* API URL */}
        <div>
          <p className="text-sm font-medium">API URL*</p>
          <Input
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="Enter API URL"
          />
          {errors.apiUrl && (
            <p className="text-red-500 text-sm">{errors.apiUrl}</p>
          )}
        </div>

        {/* Server IP */}
        <div>
          <p className="text-sm font-medium">Server IP*</p>
          <Input
            value={serverIp}
            onChange={(e) => setServerIp(e.target.value)}
            placeholder="Enter Server IP"
          />
          {errors.serverIp && (
            <p className="text-red-500 text-sm">{errors.serverIp}</p>
          )}
        </div>

        {/* Iframe URL */}
        <div>
          <p className="text-sm font-medium">Iframe URL*</p>
          <Input
            value={iframeUrl}
            onChange={(e) => setIframeUrl(e.target.value)}
            placeholder="Enter Iframe URL"
          />
          {errors.iframeUrl && (
            <p className="text-red-500 text-sm">{errors.iframeUrl}</p>
          )}
        </div>

        {/* BUTTON */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full py-2 rounded-md text-white font-medium ${
            loading ? "bg-gray-400" : "bg-primary"
          }`}
        >
          {isEdit ? "Update Terminal" : "Add Terminal"}
        </button>
      </div>
    </Modal>
  );
};

export default TerminalFormModal;
